import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useRef,
  useImperativeHandle,
} from 'react'
import getTableTransformSchemaAndFormData, {
  getListObject,
} from '../../../../lib/utils/get-table-transform-schema-and-form-data'
import ArrayTable from '../../json-form-array-table'
import FormDataEditor from '../../v2/json-form-object-editor'
import AdaptorFormDataEditor from '../../v2/json-form-object-editor/adaptor'
import { IDefaultJsonFormProps } from './interface'
import { Skeleton, Tabs } from 'antd'
import {
  createUISchemaHorizontal,
  createUISchemaVertical,
} from '../../../../lib/utils/grid-fild-orientation'

// По аналогии filter для Array. Вызывает filter([key, value], index)
export function objectFilter(object, cb) {
  return Object.fromEntries(Object.entries(object).filter(cb))
}

// По аналогии map для Array. Вызывает map([key, value], index)
// По всей видимости метод бесполезный, в новых компонентах customTabs всегда отсутствует
function objectMap(object: Record<string, any>, cb: (entry: [string, any]) => [string, any]) {
  return Object.fromEntries(Object.entries(object).map(cb) as Array<[string, any]>)
}

function DefaultJsonFormEditor(
  { formData, schema, onChange, orientation = 'vertical', customTabs = {} }: IDefaultJsonFormProps,
  ref,
) {
  const [localFormData, setLocalFormData] = useState(formData)
  const [UISchema, setUISchema] = useState<any>(null)
  const formRef = useRef<any>()

  useEffect(() => {
    setLocalFormData(formData)
    return () => {}
  }, [formData])

  useEffect(() => {
    setUISchema(
      orientation == 'horizontal'
        ? createUISchemaHorizontal(schema)
        : createUISchemaVertical(schema),
    )
    return () => {}
  }, [schema])

  const [selectedProps, selectedObjectProps, formSchema] = useMemo(() => {
    const { tables, newSchema } = getTableTransformSchemaAndFormData(schema, localFormData)
    const propertiesNotRootObject = objectFilter(newSchema.properties, ([, value]) => {
      return !(value?.type === 'object')
    })
    const objectProps = getListObject(schema)
    return [tables, objectProps, { ...newSchema, properties: propertiesNotRootObject }]
  }, [schema, localFormData])

  const selectedVirtualTabs = useMemo(() => {
    const customTabsVirtual = objectFilter(customTabs, ([, value]) => value?.isVirtual)
    const customTabsMap = objectMap(customTabsVirtual, ([name, value]) => [
      name,
      { ...value, name },
    ])
    return Object.values(customTabsMap)
  }, [customTabs])

  const onFormData = useCallback(
    ({ formData }) => {
      setLocalFormData(formData)
      onChange({ formData, schema })
    },
    [onChange, schema],
  )

  useImperativeHandle(ref, () => ({
    validateFields: params => formRef?.current?.validateFields(params),
  }))

  return (
    <>
      {UISchema ? (
        <FormDataEditor
          ref={formRef}
          formData={localFormData}
          schema={formSchema}
          onChange={onFormData}
          UISchema={UISchema}
          orientation={orientation}
        />
      ) : (
        <Skeleton active />
      )}
      {selectedProps.length > 0 ||
      selectedObjectProps.length > 0 ||
      selectedVirtualTabs.length > 0 ? (
        <Tabs
          items={[
            ...selectedProps.map(({ name }, i) => {
              const CustomTab = customTabs[name]
              if (name in customTabs) {
                return {
                  key: `${name}-${i}`,
                  label: CustomTab.title || name,
                  children: (
                    <CustomTab
                      name={name}
                      schema={schema}
                      formData={localFormData}
                      onChange={onFormData}
                    />
                  ),
                }
              }
              return {
                key: `${name}-${i}`,
                label: name,
                children: (
                  <ArrayTable
                    name={name}
                    schema={schema}
                    formData={localFormData}
                    onChange={onFormData}
                  />
                ),
              }
            }),
            ...selectedObjectProps.map(({ name }, i) => {
              const CustomTab = customTabs[name]
              if (name in customTabs) {
                return {
                  key: `${name}-object-${i}`,
                  label: CustomTab.title || name,
                  children: (
                    <CustomTab
                      name={name}
                      schema={schema}
                      formData={localFormData}
                      onChange={onFormData}
                    />
                  ),
                }
              }
              return {
                key: `${name}-object-${i}`,
                label: name,
                children: (
                  <AdaptorFormDataEditor
                    name={name}
                    schema={schema}
                    formData={localFormData}
                    onChange={onFormData}
                  />
                ),
              }
            }),
            ...selectedVirtualTabs.map(({ name, schema: tabSchema, render: Render }, i) => ({
              key: `${name}-virtual-${i}`,
              label: Render?.title || name,
              children: <Render name={name} schema={tabSchema} />,
            })),
          ]}
        />
      ) : null}
    </>
  )
}

export const DefaultJsonForm = forwardRef(DefaultJsonFormEditor)
